import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useUser } from './UserContext';
import { useAuth } from '../hooks/useAuth';

interface OnboardingContextType {
  showOnboarding: boolean;
  setShowOnboarding: (show: boolean, defaultTab?: number, redirectOnSave?: boolean) => void;
  shouldShowOnboarding: boolean;
  checkOnboardingRequirements: () => boolean;
  defaultTab: number;
  redirectOnSave: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user, loading: userLoading } = useUser();
  const { isAuthenticated } = useAuth();
  const [showOnboarding, setShowOnboardingState] = useState(false);
  const [defaultTab, setDefaultTab] = useState(0);
  const [redirectOnSave, setRedirectOnSave] = useState(true);

  // Function to validate that user has at least 6 courses with grades across any grade level
  const hasMinimumCourses = (): boolean => {
    if (!user || !user.courses || Object.keys(user.courses).length === 0) {
      return false;
    }

    // Get all course entries with their course codes
    const allCourseEntries = Object.entries(user.courses).map(([courseCode, courseData]: [string, any]) => ({
      courseCode,
      grade: courseData.grade,
      grades: courseData.grades
    }));

    // Filter courses that have both a course code and a grade value
    const coursesWithGrades = allCourseEntries.filter(course => 
      course.courseCode && course.grades && course.grades > 0
    );
    
    // Check if user has at least 6 courses with grades across any grade level
    return coursesWithGrades.length >= 6;
  };

  // Check if user has provided all required onboarding information
  const checkOnboardingRequirements = (): boolean => {
    if (!user) {
      return false;
    }

    // Check if all required profile fields are present
    const hasUserType = user.userType !== undefined && user.userType !== null;
    const hasProvince = !!user.province && user.province.trim() !== '';
    const hasHighSchool = !!user.schoolName && user.schoolName.trim() !== '';
    const hasClassYear = !!user.classYear && user.classYear.trim() !== '';

    const hasProfileData = hasUserType && hasProvince && hasHighSchool && hasClassYear;

    // If user is missing any required profile fields, show onboarding on tab 0
    if (!hasProfileData) {
      return true;
    }

    // If profile is complete but courses are missing, show onboarding on tab 1
    if (!hasMinimumCourses()) {
      return true;
    }

    return false; // All requirements met
  };

  // Determine if onboarding should be shown
  const shouldShowOnboarding = checkOnboardingRequirements();

  // Set the default tab based on what's missing (in useEffect to avoid side effects during render)
  useEffect(() => {
    if (!user) return;

    const hasUserType = user.userType !== undefined && user.userType !== null;
    const hasProvince = !!user.province && user.province.trim() !== '';
    const hasHighSchool = !!user.schoolName && user.schoolName.trim() !== '';
    const hasClassYear = !!user.classYear && user.classYear.trim() !== '';

    const hasProfileData = hasUserType && hasProvince && hasHighSchool && hasClassYear;

    if (shouldShowOnboarding) {
      // If profile is incomplete, start on tab 0
      if (!hasProfileData) {
        setDefaultTab(0);
      } else {
        // If profile is complete but courses are missing, start on tab 1
        setDefaultTab(1);
      }
    }
  }, [user, shouldShowOnboarding]);

  // Show onboarding modal when user is authenticated, user data is loaded, and requirements are not met
  useEffect(() => {
    if (isAuthenticated && !userLoading && user && shouldShowOnboarding) {
      setShowOnboardingState(true);
    }
  }, [isAuthenticated, userLoading, user, shouldShowOnboarding, showOnboarding]);

  // Custom setShowOnboarding function that accepts optional defaultTab and redirectOnSave
  const setShowOnboarding = (show: boolean, tab?: number, redirect?: boolean) => {
    setShowOnboardingState(show);
    if (show && tab !== undefined) {
      setDefaultTab(tab);
    }
    if (show && redirect !== undefined) {
      setRedirectOnSave(redirect);
    }
  };

  const value: OnboardingContextType = {
    showOnboarding,
    setShowOnboarding,
    shouldShowOnboarding,
    checkOnboardingRequirements,
    defaultTab,
    redirectOnSave
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
